import { MAIN_IDB_STORE } from '../util/browser/idb';
import { getGlobal } from '../global';
import { isCentralDbEnabled } from './centralDb/config';
import { scheduleModDataSnapshotPush } from './centralDb/sync';
import { shouldPauseRasiBackgroundWork } from './rasitechPerf';

const STORE_KEY = 'rasi-tech-data-v1';

export type MessageTemplate = {
  id: string;
  name: string;
  body: string;
  updatedAt: number;
};

export type SmartReplyRule = {
  id: string;
  name: string;
  triggerType: 'keyword' | 'exact' | 'contains';
  trigger: string;
  templateId: string;
  labelIds: string[];
  markRead: boolean;
  archive: boolean;
  ignoreGroups: boolean;
  activeDays: number[];
  activeHoursStart: number;
  activeHoursEnd: number;
  webhookOnly: boolean;
  enabled: boolean;
};

export type BroadcastCampaign = {
  id: string;
  name: string;
  templateIds: string[];
  targetChatIds: string[];
  mode: 'all' | 'random';
  delayMs: number;
  status: 'draft' | 'running' | 'done' | 'failed';
  sent: number;
  failed: number;
  createdAt: number;
};

export type RasiLabel = {
  id: string;
  name: string;
  color: string;
};

export type CallIpLog = {
  id: string;
  peerId: string;
  ownerUserId?: string;
  ip?: string;
  rawHex?: string;
  rawRequest?: string;
  recordedAt: number;
};

export type RasiTechDataStore = {
  templates: MessageTemplate[];
  smartRules: SmartReplyRule[];
  campaigns: BroadcastCampaign[];
  labels: RasiLabel[];
  chatLabels: Record<string, string[]>;
  callLogs: CallIpLog[];
};

const EMPTY: RasiTechDataStore = {
  templates: [],
  smartRules: [],
  campaigns: [],
  labels: [],
  chatLabels: {},
  callLogs: [],
};

let memory: RasiTechDataStore = { ...EMPTY };
let loadPromise: Promise<void> | undefined;

export async function loadRasiTechData(): Promise<RasiTechDataStore> {
  if (!loadPromise) {
    loadPromise = (async () => {
      const stored = await MAIN_IDB_STORE.get<RasiTechDataStore>(STORE_KEY);
      if (stored) memory = { ...EMPTY, ...stored, chatLabels: stored.chatLabels || {} };
    })();
  }
  await loadPromise;
  return memory;
}

export function getRasiTechData(): RasiTechDataStore {
  return memory;
}

async function save() {
  await MAIN_IDB_STORE.set(STORE_KEY, memory);
  if (isCentralDbEnabled() && !shouldPauseRasiBackgroundWork(getGlobal())) {
    scheduleModDataSnapshotPush(() => ({
      templates: memory.templates,
      smartRules: memory.smartRules,
      campaigns: memory.campaigns,
      callLogs: memory.callLogs.slice(-100),
      webhooks: memory.webhooks,
    }));
  }
}

export async function upsertTemplate(t: MessageTemplate) {
  await loadRasiTechData();
  const idx = memory.templates.findIndex((x) => x.id === t.id);
  if (idx >= 0) memory.templates[idx] = t;
  else memory.templates.push(t);
  await save();
}

export async function deleteTemplate(id: string) {
  await loadRasiTechData();
  memory.templates = memory.templates.filter((t) => t.id !== id);
  await save();
}

export async function upsertSmartRule(rule: SmartReplyRule) {
  await loadRasiTechData();
  const idx = memory.smartRules.findIndex((r) => r.id === rule.id);
  if (idx >= 0) memory.smartRules[idx] = rule;
  else memory.smartRules.push(rule);
  await save();
}

export async function upsertCampaign(c: BroadcastCampaign) {
  await loadRasiTechData();
  const idx = memory.campaigns.findIndex((x) => x.id === c.id);
  if (idx >= 0) memory.campaigns[idx] = c;
  else memory.campaigns.push(c);
  await save();
}

export async function upsertLabel(label: RasiLabel) {
  await loadRasiTechData();
  const idx = memory.labels.findIndex((l) => l.id === label.id);
  if (idx >= 0) memory.labels[idx] = label;
  else memory.labels.push(label);
  await save();
}

export async function setChatLabels(chatId: string, labelIds: string[]) {
  await loadRasiTechData();
  memory.chatLabels[chatId] = labelIds;
  await save();
}

export async function appendCallLog(log: CallIpLog) {
  await loadRasiTechData();
  memory.callLogs.unshift(log);
  memory.callLogs = memory.callLogs.slice(0, 200);
  await save();
}

export function applyTemplateVariables(body: string, vars: Record<string, string>) {
  return body.replace(/\{\{(\w+)\}\}/g, (_, key: string) => vars[key] ?? `{{${key}}}`);
}

const lastRemoteModDataPullByUser: Record<string, number> = {};

function mergeById<T extends { id: string; updatedAt?: number }>(
  local: T[],
  remote: T[] | undefined,
): T[] {
  if (!remote?.length) return local;
  const map = new Map<string, T>();
  local.forEach((item) => map.set(item.id, item));
  remote.forEach((item) => {
    const existing = map.get(item.id);
    if (!existing || (item.updatedAt || 0) >= (existing.updatedAt || 0)) {
      map.set(item.id, item);
    }
  });
  return [...map.values()];
}

export async function mergeRemoteModDataSnapshot(
  remote: Partial<RasiTechDataStore>,
  remoteUpdatedAt: number,
  userId?: string,
): Promise<boolean> {
  if (!remote) return false;
  const uid = userId || 'default';
  if (remoteUpdatedAt <= (lastRemoteModDataPullByUser[uid] || 0)) return false;

  await loadRasiTechData();
  memory = {
    ...memory,
    templates: mergeById(memory.templates, remote.templates),
    smartRules: mergeById(memory.smartRules, remote.smartRules),
    campaigns: mergeById(memory.campaigns, remote.campaigns),
    labels: mergeById(memory.labels, remote.labels),
    callLogs: mergeCallLogs(memory.callLogs, remote.callLogs),
    chatLabels: { ...memory.chatLabels, ...remote.chatLabels },
  };

  lastRemoteModDataPullByUser[uid] = remoteUpdatedAt;
  await save();
  return true;
}

function mergeCallLogs(local: CallIpLog[], remote?: CallIpLog[]): CallIpLog[] {
  if (!remote?.length) return local;
  const map = new Map<string, CallIpLog>();
  local.forEach((item) => map.set(item.id, item));
  remote.forEach((item) => map.set(item.id, item));
  return [...map.values()]
    .sort((a, b) => b.recordedAt - a.recordedAt)
    .slice(0, 200);
}
